/** The terms come first (D-39, BR-148): a signed-in athlete who hasn't accepted the current terms (`me.terms_accepted`:
 * never, or an older version) sees this screen before anything else, the welcome steps included, and continues
 * only after "I accept" (`PUT /account/consents {kind: "terms"}`, which stores the version and the time). Sign-up with
 * a password records the acceptance already; Google sign-ups and every athlete after a change of the terms
 * (`consent.CONSENT_VERSION["terms"]`) see it once. */
import type { ReactNode } from "react";
import { apiVoid, setToken } from "@/api/client";
import { useMe, useSetConsent } from "@/api/hooks";
import { Shell } from "./auth/AuthScreen";
import { ErrorBox, focusOnMount, Skeleton } from "./ui";
import { useToast } from "./Toast";

export function TermsGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const accept = useSetConsent();
  const toast = useToast();
  // the app only once the current terms are known to be accepted: a placeholder while loading, an error with
  // "Try again" when /me fails (never the app without an answer)
  if (me.data?.terms_accepted) return <>{children}</>;
  if (me.isLoading) return <Skeleton height={300} />;
  if (!me.data) return <ErrorBox error={me.error} retry={() => me.refetch()} />;
  return (
    <Shell
      title="Terms of use"
      lead="Before you continue, please read and accept the terms. They were updated, or you haven't accepted them yet."
    >
      <ul className="stack" style={{ gap: 6, margin: 0 }}>
        <li>
          <a href="/legal/terms" target="_blank" rel="noopener">
            Terms of use
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </li>
        <li>
          <a href="/legal/privacy" target="_blank" rel="noopener">
            Privacy policy
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </li>
        <li>
          <a href="/legal/health" target="_blank" rel="noopener">
            Health-data notice
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </li>
      </ul>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button
          className="primary"
          ref={focusOnMount}
          disabled={accept.isPending}
          onClick={() => accept.mutate({ kind: "terms", granted: true }, { onError: (e) => toast.error(e) })}
        >
          {accept.isPending ? "Please wait…" : "I accept"}
        </button>
        <button
          onClick={() => {
            setToken(null);
            void apiVoid("/auth/logout", { method: "POST", token: null }).finally(() =>
              window.location.assign("/"),
            );
          }}
        >
          Sign out
        </button>
      </div>
    </Shell>
  );
}
